import { UserRole } from '@lmc/ts-common'
import type { ReactNode } from 'react'
import { Suspense } from 'react'
import styled from 'styled-components'
import ProfileFieldRow from '../../../../../screens/Organisation/screens/TeamMemberProfileScreen/components/v2/ProfileFieldRow'
import Field from '../../../../fields'
import {
  BLOCK_NAME_CHANGE_NOTE,
  INCIDENT_EMAIL_NOTIFICATION_NOTE,
  INCIDENT_EMAIL_NOTIFICATION_OPTIONS,
  STATUS_NOTE,
  STATUS_OPTIONS
} from '../constants'
import EditProfileCard, { CardFields, segmentedRadio } from '../EditProfileCard'
import JobTitleField from '../JobTitleField'

const RequiredNote = styled.div`
  font-size: 16px;
  color: ${({ theme }) => theme.color.neutral.color4};
  margin-bottom: 20px;
`

const Asterisk = styled.span`
  color: ${({ theme }) => theme.color.status.danger};
  font-weight: 600;
`

type AccountDetailsCardProps = {
  emailIncidentNotificationsFlagEnabled?: boolean
  fields: Record<string, any>
  formId: string
  groupAccessManagementEnabled?: boolean
  homesWithHomeGroup: Array<{ id: string; name: string }>
  isGroupUser: boolean
  jobTitle?: string
  onJobTitleChange: (value: string) => void
  renderAvatar?: () => ReactNode
  renderPinField: () => ReactNode
  renderSSOResetPassword: () => ReactNode
  renderSummaryField: () => ReactNode
  targetEditable: boolean
  testId?: string
}

const AccountDetailsCard = ({
  fields,
  formId,
  targetEditable,
  groupAccessManagementEnabled,
  emailIncidentNotificationsFlagEnabled,
  homesWithHomeGroup,
  isGroupUser,
  jobTitle,
  onJobTitleChange,
  renderAvatar,
  renderSummaryField,
  renderPinField,
  renderSSOResetPassword,
  testId
}: AccountDetailsCardProps) => {
  const blockNameChange = fields.homes && fields.homes.length > 1
  const hasMoreHomes = !!homesWithHomeGroup?.length
  const showIncidentNotifications =
    emailIncidentNotificationsFlagEnabled &&
    (fields.role === UserRole.careHomeAdmin || fields.role === UserRole.management)

  return (
    <EditProfileCard title="Account details" testId={testId}>
      <RequiredNote>
        Required fields are marked with <Asterisk>*</Asterisk>
      </RequiredNote>
      {renderAvatar?.()}
      <Suspense fallback={<div>Loading...</div>}>
        <CardFields>
          <Field
            formId={formId}
            keys={['name.first', 'name.last']}
            label="Name"
            note={blockNameChange && BLOCK_NAME_CHANGE_NOTE}
            placeholders={['First name', 'Last name']}
            required
            type="multiText"
            disabled={blockNameChange}
            values={fields}
            maxlength={35}
          />
          <JobTitleField
            value={jobTitle}
            onChange={onJobTitleChange}
            testId={testId ? `${testId}-job-title` : undefined}
          />
          <Field
            className="email-field"
            formId={formId}
            inputType="email"
            keys="email"
            label="Email"
            required
            type="text"
            validationTypes="email"
            disabled={!targetEditable}
            values={fields}
          />
          {renderSSOResetPassword()}
          {renderPinField()}
          <Field
            formId={formId}
            keys="active"
            label="Status"
            options={STATUS_OPTIONS}
            note={STATUS_NOTE}
            required
            type="radios"
            disabled={!targetEditable}
            values={fields}
            {...segmentedRadio}
          />
          {groupAccessManagementEnabled && !isGroupUser && hasMoreHomes && (
            <Field
              formId={formId}
              keys="homes"
              label="Sites"
              options={homesWithHomeGroup.map((home) => ({
                value: home.id,
                label: home.name
              }))}
              required
              type="checkbox"
              disabled={!targetEditable}
              values={fields}
            />
          )}
          {isGroupUser &&
            groupAccessManagementEnabled &&
            fields?.homes?.length > 0 && (
              <ProfileFieldRow
                label="Sites"
                value={
                  (fields?.homes || [])
                    ?.map(
                      (homeId: string) =>
                        homesWithHomeGroup?.find((h) => h.id === homeId)?.name
                    )
                    .filter(Boolean)
                    .join(', ') || '-'
                }
              />
            )}
          {showIncidentNotifications && (
            <Field
              className="email-incident-notifications-field"
              formId={formId}
              keys="emailIncidentNotificationsEnabled"
              label="Incident Email Notifications"
              options={INCIDENT_EMAIL_NOTIFICATION_OPTIONS}
              note={INCIDENT_EMAIL_NOTIFICATION_NOTE}
              required
              type="radios"
              values={fields}
              {...segmentedRadio}
            />
          )}
          {renderSummaryField()}
        </CardFields>
      </Suspense>
    </EditProfileCard>
  )
}

export default AccountDetailsCard
